import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Alert = ({ children, variant = 'default', className = '' }) => {
  const baseStyles = 'px-4 py-3 rounded-lg flex items-center gap-2';
  const variantStyles = {
    default: 'bg-gray-100 dark:bg-gray-800/50 text-gray-800 dark:text-gray-200',
    destructive: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50',
    warning: 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    success: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
  };

  return (
    <div className={`${baseStyles} ${variantStyles[variant]} ${className}`}>
      {variant === 'destructive' && <AlertTriangle className="h-4 w-4 flex-shrink-0" />}
      {variant === 'success' && <CheckCircle className="h-4 w-4 flex-shrink-0" />}
      {children}
    </div>
  );
};

export const AlertDescription = ({ children }) => (
  <div className="text-sm font-medium">{children}</div>
);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const BaseRegister = ({ 
  children, 
  currentStep, 
  totalSteps, 
  handleNext, 
  handleBack,
  handleFinish,
  loading,
  title = "Registro",
  errors = {}
}) => {
  const navigate = useNavigate();

  const handleCancel = () => {
    if (window.confirm('¿Está seguro que desea cancelar el registro?')) {
      navigate('/login');
    }
  };

  // Función para determinar el nombre del paso según el tipo de registro y número
  const getStepName = (stepNumber) => {
    // Para ClientRegister (4 pasos) - sin cambios
    if (totalSteps === 4) {
      switch (stepNumber) {
        case 1: return 'Email';
        case 2: return 'Contraseña';
        case 3: return 'Información';
        case 4: return 'Términos';
        default: return `Paso ${stepNumber}`;
      }
    } 
    // Para SellerRegister (6 pasos) - actualizado
    else if (totalSteps === 6) {
      switch (stepNumber) {
        case 1: return 'Email';
        case 2: return 'Contraseña';
        case 3: return 'Personal';
        case 4: return 'Redes';
        case 5: return 'Experiencia';
        case 6: return 'Finalizar';
        default: return `Paso ${stepNumber}`;
      }
    }
    // Para InmRegister (7 pasos) - actualizado
    else if (totalSteps === 7) {
      switch (stepNumber) {
        case 1: return 'Email';
        case 2: return 'Contraseña';
        case 3: return 'Empresa';
        case 4: return 'Ubicación';
        case 5: return 'Contacto';
        case 6: return 'Experiencia';
        case 7: return 'Finalizar';
        default: return `Paso ${stepNumber}`;
      }
    }
    // Valor predeterminado para cualquier otro caso
    return `Paso ${stepNumber}`;
  };

  // Step indicator component - completamente rediseñado para mostrar mejor los pasos
  const StepIndicator = () => {
    // Para pantallas pequeñas, mostrar un enfoque más compacto
    if (window.innerWidth < 640) {
      return (
        <div className="flex justify-center my-4">
          <div className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg backdrop-blur-sm">
            <span className="font-medium text-indigo-700 dark:text-indigo-300">
              Paso {currentStep} de {totalSteps}
            </span>
          </div>
        </div>
      );
    }

    // Para pantallas medianas y grandes, mostrar todos los pasos
    return (
      <div className="flex items-center justify-center my-6 overflow-x-auto py-1 px-2">
        <div className="flex items-center min-w-full justify-center">
          {[...Array(totalSteps)].map((_, index) => {
            const stepNumber = index + 1;
            const isActive = stepNumber === currentStep;
            const isCompleted = stepNumber < currentStep;
            
            return (
              <React.Fragment key={stepNumber}>
                {/* Connector line */}
                {index > 0 && (
                  <div 
                    className={`h-1 w-3 sm:w-4 md:w-6 rounded-full transition-all duration-300 ${
                      isCompleted ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
                
                {/* Step circle with label underneath */}
                <div className="flex flex-col items-center">
                  <div 
                    className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 rounded-full transition-all duration-300 ${
                      isActive 
                        ? 'bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500 text-white shadow-md dark:shadow-black/25' 
                        : isCompleted 
                          ? 'bg-indigo-600 dark:bg-indigo-500 text-white' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <span className="text-xs font-medium">{stepNumber}</span>
                    )}
                  </div>
                  {/* Label with fixed width and truncation for longer text */}
                  <span 
                    className={`mt-1 text-[9px] sm:text-[10px] md:text-xs w-14 text-center truncate ${
                      isActive ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {getStepName(stepNumber)}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16 px-4 sm:px-6 lg:px-8">
      <motion.div 
        className="max-w-md mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-2xl shadow-xl dark:shadow-black/50 overflow-hidden border border-white/50 dark:border-gray-700/50">
          {/* Header */}
          <div className="border-b border-gray-100 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCancel}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                <span>Cancelar</span>
              </motion.button>
              <h1 className="text-xl font-semibold bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-400 dark:to-blue-400 bg-clip-text text-transparent">
                {title}
              </h1>
              <div className="w-16"></div>
            </div>
          </div>

          {/* Step Indicator */}
          <StepIndicator />

          {/* Form Content */}
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="px-6 py-6"
            >
              {children}

              {/* Error Messages */}
              {Object.keys(errors).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <Alert variant="destructive" className="mt-4">
                    <div className="space-y-1">
                      {Object.entries(errors).map(([key, error]) => (
                        <AlertDescription key={key}>{error}</AlertDescription>
                      ))}
                    </div>
                  </Alert>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-between p-6 border-t border-gray-100 dark:border-gray-700">
            {currentStep > 1 ? (
              <motion.button
                type="button"
                onClick={handleBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Anterior
              </motion.button>
            ) : (
              <div></div>
            )}
            
            {currentStep < totalSteps ? (
              <motion.button
                type="button"
                onClick={handleNext}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-6 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 dark:from-indigo-500 dark:to-blue-500
                        text-white rounded-xl font-medium transition-all duration-300
                        hover:shadow-lg dark:hover:shadow-black/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ArrowRight className="w-5 h-5 ml-2" />
              </motion.button>
            ) : (
              <motion.button
                type="button"
                onClick={handleFinish}
                disabled={loading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-500 dark:to-emerald-500
                        text-white rounded-xl font-medium transition-all duration-300
                        hover:shadow-lg dark:hover:shadow-black/25 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <span className="relative flex h-3 w-3 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                    </span>
                    Registrando...
                  </>
                ) : (
                  <>
                    Completar Registro
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </motion.button>
            )}
          </div>
        </motion.div>

        <p className="text-center text-sm text-gray-600 dark:text-gray-400 mt-4 backdrop-blur-sm bg-white/80 dark:bg-gray-800/80 p-2 rounded-lg border border-white/50 dark:border-gray-700/50">
          ¿Ya tienes una cuenta?{' '}
          <motion.button
            whileHover={{ scale: 1.05 }}
            onClick={() => navigate('/login')}
            className="font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
          >
            Inicia sesión aquí
          </motion.button>
        </p>
      </motion.div>
    </div>
  );
};

BaseRegister.propTypes = {
  children: PropTypes.node.isRequired,
  currentStep: PropTypes.number.isRequired,
  totalSteps: PropTypes.number.isRequired,
  handleNext: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  handleFinish: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  errors: PropTypes.object
};

export default BaseRegister;